import { ChangeDetectionStrategy, Component, inject, Input, OnDestroy } from '@angular/core';
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { APP_CONFIG, DeviceEnum, FrameIDTypeEnum, SearchItems } from '../../core';
import { ClonedListModalComponent, ReceivedCloneModalComponent } from '../../plan';
import { ListService, PlanService, UserService } from '../../services';
import { BaseComponent, ConfirmationComponent } from '../../shared';


/**
* Received plan actions component on received plan table.
*/
@Component({
	selector: 'received-action',
	templateUrl: './received-action.component.html',
	imports: [
		NgbTooltip
	],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class ReceivedActionComponent extends BaseComponent implements OnDestroy {

	private readonly config = inject(APP_CONFIG);
	private readonly modalSrv = inject(NgbModal);
	private readonly planSrv = inject(PlanService);
	private readonly listSrv = inject(ListService);
	private readonly userSrv = inject(UserService);

	@Input() set item(val: SearchItems) {
		this.plan = val;
		this.planGuid = val.planId;
		this.planId = val.planNumber;
		this.planNotes = val.caseNotes;
		this.frameId = val.frameID as FrameIDTypeEnum;
		this.device = val.device;
		this.isViewDisabled = val.device === DeviceEnum.ToBeDefined;
		this._usrSub$ = this.userSrv.isCustomerCareOtherUser(val.userId).subscribe(res => this.isReadonly = res);
	}

	plan?: SearchItems;
	planGuid?: string;
	planId?: string;
	planNotes?: string;
	frameId?: FrameIDTypeEnum;
	device?: DeviceEnum;
	isViewDisabled = true;
	isReadonly = false;

	private _usrSub$?: Subscription;

	/**
	* Navigate to view case page.
	*/
	view(): void {
		if (!this.isViewDisabled) {
			let site;
			switch (this.device) {
				case DeviceEnum.Fitbone: site = this.config.fitboneViewSite; break;
				case DeviceEnum.Tlhex: site = this.config.tlhexViewSite; break;
				default: site = this.config.nextrayViewSite; break;
			}
			globalThis.location.href = `${site}?caseGuid=${this.planGuid}`;
		}
	}

	/**
	* Open modal to clone received plan.
	*/
	openCloneModal(): void {
		if (!this.isReadonly) {
			const modalRef = this.modalSrv.open(ReceivedCloneModalComponent, { centered: true, backdrop: 'static', windowClass: 'modal-form', size: 'lg' });
			(modalRef.componentInstance as ReceivedCloneModalComponent).planGuid = this.planGuid!;
			(modalRef.componentInstance as ReceivedCloneModalComponent).planNotes = this.planNotes!;
			(modalRef.componentInstance as ReceivedCloneModalComponent).device = this.device;
			(modalRef.componentInstance as ReceivedCloneModalComponent).frameId = this.frameId;
			(modalRef.componentInstance as ReceivedCloneModalComponent).item.set(this.plan!);
		}
	}

	/**
	* Open modal to view cloned plans.
	*/
	openDetailModal(): void {
		const modalRef = this.modalSrv.open(ClonedListModalComponent, { centered: true, backdrop: 'static', windowClass: 'modal-list' });
		(modalRef.componentInstance as ClonedListModalComponent).originalPlanGuid = this.planGuid!;
		(modalRef.componentInstance as ClonedListModalComponent).originalPlanId = this.planId!;
	}

	/**
	* Open confirmation modal before delete plan.
	*/
	openDeleteModal(): void {
		if (!this.isReadonly && !this.modalSrv.hasOpenModals()) {
			const modalRef = this.modalSrv.open(ConfirmationComponent, { centered: true, backdrop: 'static' });
			(modalRef.componentInstance as ConfirmationComponent).config = {
				title: this.labels().RECEIVED_ACTION_COMPONENT_DELETE_MODAL_TITLE,
				message: this.labels().RECEIVED_ACTION_COMPONENT_DELETE_MODAL_MESSAGE,
				cancelBtn: this.labels().RECEIVED_ACTION_COMPONENT_DELETE_MODAL_NO,
				confirmBtn: this.labels().RECEIVED_ACTION_COMPONENT_DELETE_MODAL_YES
			};
			modalRef.result.then(() => this.delete()).catch(error => false);
		}
	}

	private delete(): void {
		this.planSrv.deletePlan(this.planGuid!).subscribe(() => this.listSrv.search());
	}


	ngOnDestroy(): void {
		this._usrSub$?.unsubscribe();
	}
}
